import { KitService } from '../services/kits.service';
import { kitsDTO } from '../models/kits';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { BreadcrumbsComponent } from '../../shared/components/breadcrumbs/breadcrumbs.component';
import { BreadcrumbsOptions } from '../../shared/models/breadcrumbs-model';
import { AlertComponent } from '../../shared/components/dialogs/alert/alert.component';
import { ConfirmDialogComponent } from '../../shared/components/dialogs/confirm/confirm.component';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTooltip } from '@angular/material/tooltip';
import { ItemDTO } from '../../orders/models/order';
import { startWith, map } from 'rxjs';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatCardModule } from '@angular/material/card';
import { ElementRef } from '@angular/core';
import { AfterViewInit } from '@angular/core';

@Component({
  selector: 'details-kit',
  templateUrl: './details-kit.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatSelectModule,
    MatPaginator,
    MatTableModule,
    TranslateModule,
    FlexLayoutModule,
    BreadcrumbsComponent,
    MatTooltip,
    MatAutocompleteModule,
    MatCardModule
  ],
  styleUrls: ['./details-kit.component.scss']
})
export class DetailsKitComponent implements OnInit, AfterViewInit {
  breadcrumbsData: BreadcrumbsOptions = {
    title: 'general.items',
    buttons: [
      {
        label: 'button.cancel',
        style: 'red',
        do: () => {
          this.confirmCancel();
        }
      },
      {
        label: 'button.save',
        style: 'green',
        do: () => {
          if (this.isSaveEnabled) {
            this.saveKit();
          }
        },
        disabled: true
      }
    ]
  };

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('form') kitFormViewChild: any;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private formBuilder = inject(FormBuilder);
  readonly dialog = inject(MatDialog);

  isEdit = false;
  isSaveEnabled: boolean = false;
  displayedColumns: string[] = ['codItem', 'partNumber', 'item', 'category', 'manufacturer', 'action'];

  editKit: kitsDTO = {
    id: 0,
    description: '',
    pathImage: null,
    items: [],
    active: true
  };

  items: ItemDTO[] = [];
  filteredItems: ItemDTO[] = [];
  kitItems: ItemDTO[] = [];
  itemControl = new FormControl<ItemDTO | string>('', Validators.required);
  dataSource = new MatTableDataSource<ItemDTO>(this.kitItems);
  kitForm!: FormGroup;
  selectedFile: File | null = null;
  imagePreview: string | ArrayBuffer | null = null;

  pageable: PageEvent = {
    pageIndex: 0,
    pageSize: 5,
    length: 0
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private kitService: KitService
  ) {}

  ngOnInit() {
    this.kitForm = this.formBuilder.group({
      description: ['', Validators.required]
    });
    this.kitForm.valueChanges.subscribe(() => {
      this.checkSaveConditions();
    });

    this.pageChange(this.pageable);
    this.listItems();

    this.itemControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterItems(value ?? undefined))
    ).subscribe(filtered => {
      this.filteredItems = filtered;
    });

    if (this.route.snapshot.params['id']) {
      this.isEdit = true;
      this.editKit.id = this.route.snapshot.params['id'];
      this.startUpdate(this.editKit.id);
    }

    this.updateFilteredItems();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  private updateFilteredItems() {
    this.filteredItems = this.filterItems(this.itemControl.value ?? '');
  }

  pageChange(event: PageEvent) {
    this.pageable.pageIndex = event.pageIndex;
    this.pageable.pageSize = event.pageSize;
  }

  saveImage(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const maxSizeMB = 5;
      const maxSizeBytes = maxSizeMB * 1024 * 1024;

      if (file.size > maxSizeBytes) {
        this.dialog.open(AlertComponent, {
          disableClose: true,
          data: {
            title: 'alert.information',
            message: 'item.imageTooLarge'
          }
        });
        this.selectedFile = null;
        this.fileInput.nativeElement.value = '';
        return;
      }

      this.selectedFile = file;
      this.imagePreview = null;

      const reader = new FileReader();
      reader.onload = () => {
        this.editKit.pathImage = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  triggerFileInput(): void {
    if (this.fileInput) {
      this.fileInput.nativeElement.click();
    }
  }

  private getErrorMessage(e: any, defaultError: string): string {
    if (e.status === 400) return 'item.imageTooLarge';
    if (e.status === 409) return 'kit.kitConflict';
    return defaultError;
  }

  saveKit() {
    if (!this.kitForm.valid || this.kitItems.length === 0) {
      this.dialog.open(AlertComponent, {
        disableClose: true,
        data: {
          title: 'alert.information',
          message: 'kit.formInvalid'
        }
      });
      return;
    }

    const maxSizeMB = 5;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (this.selectedFile && this.selectedFile.size > maxSizeBytes) {
      this.dialog.open(AlertComponent, {
        disableClose: true,
        data: {
          title: 'alert.information',
          message: 'item.imageTooLarge'
        }
      });
      this.selectedFile = null;
      this.fileInput.nativeElement.value = '';
      return;
    }

    const formData = new FormData();
    const kitData = {
      description: this.kitForm.get('description')?.value,
      active: true,
      items: this.kitItems.map(item => item.id)
    };

    formData.append('kitForm', new Blob([JSON.stringify(kitData)], { type: 'application/json' }));
    if (this.selectedFile) {
      formData.append('file', this.selectedFile);
    }

    if (this.isEdit) {
      this.kitService.updateKit(formData, this.editKit.id!).subscribe({
        next: () => {
          this.dialog.open(AlertComponent, {
            disableClose: true,
            data: {
              title: 'alert.information',
              param: { description: this.kitForm.get('description')?.value },
              message: 'kit.kitUpdatedSuccess'
            }
          });
          this.navigate();
        },
        error: (e) => {
          const errorMessage = this.getErrorMessage(e, 'kit.kitUpdatedError');
          this.dialog.open(AlertComponent, {
            disableClose: true,
            data: {
              title: 'alert.information',
              param: { description: this.kitForm.get('description')?.value },
              message: errorMessage
            }
          });
        }
      });
    } else {
      this.kitService.createKit(formData).subscribe({
        next: () => {
          this.dialog.open(AlertComponent, {
            disableClose: true,
            data: {
              title: 'alert.information',
              param: { description: this.kitForm.get('description')?.value },
              message: 'kit.kitAddedSuccess'
            }
          });
          this.navigate();
        },
        error: (e) => {
          const errorMessage = this.getErrorMessage(e, 'kit.kitAddedError');
          this.dialog.open(AlertComponent, {
            disableClose: true,
            data: {
              title: 'alert.information',
              param: { description: this.kitForm.get('description')?.value },
              message: errorMessage
            }
          });
        }
      });
    }
  }

  startUpdate(id?: number) {
    this.kitService.getKitById(id!).subscribe({
      next: (kit) => {
        this.editKit = kit;
        this.kitForm.patchValue({
          description: kit.description
        });

        if (kit.id) {
          this.loadKitImage(kit.id);
        } else {
          this.editKit.pathImage = null;
          this.imagePreview = null;
        }

        this.kitItems = [...kit.items];
        this.dataSource.data = [...this.kitItems];
        this.updateFilteredItems();
        this.checkSaveConditions();
      },
      error: (e) => {
        this.dialog.open(AlertComponent, {
          disableClose: true,
          data: {
            title: 'alert.information',
            message: 'kit.kitGetError'
          }
        });
      }
    });
  }

  loadKitImage(id?: number) {
    if (id) {
      this.kitService.getKitImage(id).subscribe({
        next: (blob: Blob) => {
          if (blob && blob.size > 0) {
            const reader = new FileReader();
            reader.onload = () => {
              this.editKit.pathImage = reader.result as string;
            };
            reader.readAsDataURL(blob);
          } else {
            this.editKit.pathImage = null;
          }
        },
        error: () => {
          this.editKit.pathImage = null;
        }
      });
    } else {
      this.editKit.pathImage = null;
    }
  }

  addItem(): void {
    const currentValue = this.itemControl.value;
    if (currentValue && typeof currentValue === 'object') {
      const alreadyExist = this.kitItems.find(i => i.id === currentValue.id);
      if (!alreadyExist) {
        this.kitItems.push(currentValue);
        this.dataSource.data = [...this.kitItems];
        this.pageable.length = this.kitItems.length;
        this.updateFilteredItems();
        this.checkSaveConditions();
      }
      this.itemControl.setValue('');
    }
  }

  updateItem(itemId: number) {
    this.router.navigate([`/items/${itemId}`]);
  }

  listItems(filter?: string) {
    this.kitService.getListItems(filter ?? '').subscribe({
      next: (response) => {
        this.items = response;
        this.updateFilteredItems();
      },
      error: (e) => {
        this.dialog.open(AlertComponent, {
          disableClose: true,
          data: {
            title: 'alert.information',
            message: 'item.getItemsError'
          }
        });
      }
    });
  }

  deleteItem(item: any) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'item.deleteItemConfirmation',
        param: { description: item.description },
        message: 'item.deleteItemConfirmation'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.confirm) {
        this.kitItems = this.kitItems.filter(i => i.id !== item.id);
        this.dataSource.data = [...this.kitItems];
        this.pageable.length = this.kitItems.length;
        this.updateFilteredItems();
        this.checkSaveConditions();
      }
    });
  }

  private filterItems(filter?: string | ItemDTO): ItemDTO[] {
    if (!Array.isArray(this.items)) {
      return [];
    }

    const kitItemIds = new Set(this.kitItems.map(item => item.id));
    const searchText = typeof filter === 'string' ? filter.trim() : '';

    let filtered = this.items.filter(item => !kitItemIds.has(item.id));

    if (searchText !== '') {
      const lowercaseFilter = searchText.toLowerCase();
      filtered = filtered.filter(item =>
        (item.description ?? '').toLowerCase().includes(lowercaseFilter) ||
        (item.partNumber ?? '').toLowerCase().includes(lowercaseFilter)
      );
    }

    return filtered;
  }

  openAutocompletePanel() {
    this.updateFilteredItems();
    this.autocompleteTrigger.openPanel();
  }

  onItemSelected(item: ItemDTO): void {
    this.itemControl.setValue(item);
  }

  displayItem(item: ItemDTO): string {
    return item ? `#${item.partNumber} - ${item.description}` : '';
  }

  resetTable() {
    if (this.kitFormViewChild) {
      this.kitFormViewChild.resetForm();
    } else {
      this.kitForm.reset();
    }
    this.kitItems = [];
    this.dataSource.data = [];
    this.pageable = { pageIndex: 0, pageSize: 5, length: 0 };
  }

  private checkSaveConditions(): void {
    const description = this.kitForm.get('description')?.value;
    const hasItems = this.kitItems.length > 0;
    this.isSaveEnabled = !!description && hasItems;
    if (this.breadcrumbsData.buttons) {
      this.breadcrumbsData.buttons[1].disabled = !this.isSaveEnabled;
    }
  }

  navigate() {
    this.router.navigate(['/kits']);
  }

  confirmCancel() {
    const isEdit = this.isEdit;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: {
        title: 'alert.confirmation',
        message: isEdit
          ? 'kit.confirmCancelEdit'
          : 'kit.confirmCancelCreate'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirm) {
        this.navigate();
      }
    });
  }
}
